<template>
  <div class="flex-1 flex flex-col justify-between">
    <div class="divide-y divide-gray-200">
      <div class="space-y-6 pt-6 pb-5">
        <transition
          mode="out-in"
          enter-active-class="transform ease-out duration-300 transition"
          enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0">
          <Form
            v-if="add_new"
            :validation-schema="new_phases_journey_schema"
            :initial-values="initial_values"
            class="mt-2 space-y-2"
            @submit="createJourney">
            <span class="text-lg">{{ $t("New Journey") }}</span>
            <div class="rounded-md shadow-sm flex mb-3 items-center">
              <TextInput
                label=""
                :classes="'w-full'"
                input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :placeholder="$t('Journey name')" />
            </div>

            <div class="flex gap-2 w-full flex-row justify-between mt-3">
              <div
                class="cancel-btn w-1/2 m-0"
                @click="
                  () => {
                    add_new = false
                  }
                ">
                {{ $t("Cancel") }}
              </div>
              <button type="submit" class="primary-btn w-1/2">{{ $t("Add Journey") }}</button>
            </div>
          </Form>

          <Form v-else
                :validation-schema="schema"
                class="mt-2 space-y-6"
                :initial-values="initial_values"
                @submit="createPhase">
            <div class="rounded-md shadow-sm -space-y-px mb-4">
              <TextInput label="" name="name" type="text" :placeholder="$t('Phase name')" />
            </div>

            <div class="mb-10">
              <DualList
                v-model:current-items="selected_metrics"
                name="table_metrics"
                :show-filters="true"
                :all-items="
                  $store.state.settings.forecasting_objects_renewal_base_metrics[$utils.getObjectTypeByType(journey.type).id]
                    ? [
                      { name: $t('Actual value'), id: 's1' },
                      { name: $t('Forecasted value'), id: 's2' }
                    ].concat($store.state.metrics.filter((t) => t.object_type == journey.type).filter((m) => m?.data_type === 'Number'))
                    : $store.state.metrics.filter((t) => t.object_type == journey.type).filter((m) => m?.data_type === 'Number')
                "
                :selected-items="[]"
                :return-objects="true"
                :limit="3"
                label-attr="name"
                value-attr="id" />
            </div>

            <span class="mr-4">{{ $t("Summary Aggregation method") }}:</span>
            <Switch
              :class="[
                summary_aggregation_method == $t('sum') ? 'bg-[#9747FF]' : 'bg-[#FFBE15] dark:bg-dark-screen-background',
                'relative inline-flex flex-shrink-0 h-7 mx-auto w-[60px] border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
              ]"
              @update:modelValue="
                summary_aggregation_method == $t('sum')
                  ? (summary_aggregation_method = $t('average'))
                  : (summary_aggregation_method = $t('sum'))
              ">
              <span
                aria-hidden="true"
                :class="[
                  summary_aggregation_method && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-8'
                    : summary_aggregation_method
                      ? 'translate-x-8'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                ]"><span
                :class="{ '-ml-14': summary_aggregation_method == $t('sum'), 'ml-8': summary_aggregation_method == $t('average') }"
                class="text-xs text-white">{{ summary_aggregation_method == $t("average") ? $t("Avg") : $t("Sum") }}</span></span>
            </Switch>

            <div class="rounded-md shadow-sm w-full border-2 min-h-[120px] p-2 dark:border-dark-divider-border">
              <div class="relative">
                <label class="block text-base font-medium text-gray-700 dark:text-white">{{
                  $t("Include this Phase in other Journeys?")
                }}</label>
              </div>
              <div class="mt-1 w-full">
                <MultiSelectInputFlat
                  v-model:current-items="selected_journeys"
                  name="journeys"
                  :items="journeys.filter((f) => f.object_type == journey.object_type)"
                  :value="selected_journeys_full"
                  label-attr="name"
                  :allow-null="false"
                  value-attr="id"
                  :label="$t('Journeys')"
                  :return-objects="false"
                  :show-label="false" />
              </div>

              <div v-show="no_journeys_error" class="ml-2 mt-2 text-sm text-red-400 absolute">
                {{ $t("At least 1 journey is required") }}
              </div>

              <div class="flex justify-end items-center w-full p-1 mt-4">
                <button
                  type="button"
                  class="text-center cancel-btn border-none"
                  @click="
                    () => {
                      add_new = true
                    }
                  ">
                  {{ $t("Add New Journey") }}
                </button>
              </div>
            </div>

            <div
              :class="[filter_error ? 'border-red-400' : '']"
              class="p-2 rounded-md flex flex-col border-[1px] border-gray-300 dark:border-dark-divider-border">
              <div class="flex justify-between">
                <div class="flex items-center gap-2 relative">
                  <label class="inline text-base font-medium text-gray-700 dark:text-white">{{ $t("Phase filter") }}</label>
                  <p class="inline text-[12px] float-left font-normal text-gray-500">
                    ({{ $t("Write a filter to filter out the desired data") }}) ({{ $t("Required") }})
                  </p>
                </div>
              </div>

              <div class="h-full mb-8" style="min-height: 8rem">
                <AdvancedMetricsFilterForm
                  :object-type="journey.type"
                  @update="
                    ($event) => {
                      handleUpdateFilter($event)
                    }
                  " />
              </div>
              <span v-if="filter_error" class="text-red-400">{{ $t("Filter is required") }}</span>
            </div>
            <div class="flex gap-2 w-full flex-row justify-between">
              <button type="button" class="cancel-btn w-1/2 m-0" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
              <button type="submit" class="primary-btn w-1/2">{{ $t("Add") }}</button>
            </div>
          </Form>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import MultiSelectInputFlat from "@/components/shared/MultiSelectInputFlat.vue"
import { Form } from "vee-validate"
import dataService from "@/services/data.service"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { Switch } from "@headlessui/vue"
import DualList from "@/components/shared/DualList.vue"
import { useI18n } from "vue-i18n"

export default {
  components: {
    DualList,
    AdvancedMetricsFilterForm,
    MultiSelectInputFlat,
    TextInput,
    Form,
    Switch
  },
  props: {
    phasesJourneys: {
      type: Array,
      required: true
    },
    journey: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel", "submitted", "done"],
  setup() {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Phase name is required")),
      is_public: Yup.mixed().oneOf(["true", "false"])
    })
    const new_phases_journey_schema = Yup.object().shape({
      name: Yup.string().required(t("Phase journey name is required")),
      is_public: Yup.mixed().oneOf(["true", "false"])
    })
    const initial_values = { name: "", is_public: "true" }
    return {
      schema,
      new_phases_journey_schema,
      initial_values
    }
  },
  data() {
    return {
      summary_aggregation_method: this.$t("average"),
      selected_journeys: [],
      selected_journeys_full: [],
      selected_metrics: [],
      journeys: [],
      add_new: false,
      filter: null,
      filter_error: false,
      is_filter_valid: false,
      no_journeys_error: false,
      displayed_metrics_stats: [this.$t("Forecasted value"), this.$t("Actual value")]
    }
  },

  watch: {
    selected_journeys() {
      this.selected_journeys_full = this.selected_journeys.map((s) => this.journeys.filter((f) => f.id == s)[0])
    }
  },
  mounted() {
    this.journeys = this.$utils.clone(this.phasesJourneys)

    //initially set the selected journeys with the default selection, which is the first journey

    this.selected_journeys = [this.journey.id]
  },

  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({ ...item, value: item.value == null ? "" : item.value.trim() })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.filter = parsed_filter.filter
      this.is_filter_valid = parsed_filter.valid
    },

    createPhase(values) {
      this.filter_error = false
      values.displayed_metrics_stats = this.displayed_metrics_stats

      const solid_metrics_calc = []
      const stat_metrics_calc = []
      this.selected_metrics.forEach((m) => {
        if (m.id === "s1") {
          solid_metrics_calc.push({ name: this.$t("Actual value"), id: "s1" })
        } else if (m.id === "s2") {
          solid_metrics_calc.push({ name: this.$t("Forecasted value"), id: "s2" })
        } else {
          stat_metrics_calc.push(m.id)
        }
      })

      //"selected_journeys" is linked to the flat list and automatically updates everytime the user makes a different selection
      if (!this.selected_journeys.length) {
        this.no_journeys_error = true
      } else {
        this.no_journeys_error = false

        if (this.filter) {
          let data = { ...values, filter: this.filter }
          if (this.selected_journeys.length) {
            data.funnels = this.selected_journeys
          }
          dataService
            .createPhase({ ...data, displayed_metrics_stats: stat_metrics_calc, summary_aggregation: this.summary_aggregation_method })
            .then((res) => {
              this.$store.dispatch("data/updatePhasesSolidMetrics", {
                id: res.data.id,
                list: solid_metrics_calc
              })
              this.$emit("done", res.data)
            })
            .catch(() => {
              this.filter_error = true
            })
        } else {
          this.filter_error = true
        }
      }
    },
    createJourney(values) {
      values["object_type"] = this.journey.object_type
      dataService.addJourney(values).then((res) => {
        this.journeys.push(res.data)
        this.$emitter.emit(ALL_EMITTERS.JOURNEY_ADDED, res.data)
        this.add_new = false
      })
    }
  }
}
</script>
